import {
  FISCAL_DEVICE_SCOPE_LABELS,
  Z_REPORT_SOURCE_LABELS,
  type FiscalDevice,
  type ZReport,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateInput,
  Drawer,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, ReceiptText, RefreshCw } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import {
  useFiscalDevices,
  useShiftPermissions,
  useZReports,
  useRefreshShifts,
} from "../../../features/shifts/queries.js";
import {
  NoStations,
  ShiftStationField,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../features/shifts/shared.js";
import { compactSearch, formatMinor } from "../../../features/shifts/shift-values.js";
import {
  FiscalDeviceDialog,
  ResolveZReportDialog,
  ZReportDialog,
} from "../../../features/shifts/z-report-dialogs.js";
import {
  FISCAL_DEVICE_SCOPE_TONE,
  zDifferenceDirection,
  zDifferenceTone,
  zReportFlags,
} from "../../../features/shifts/z-report-values.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../lib/hooks.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../lib/format.js";

const Z_TABS = ["reports", "devices"] as const;
type ZTab = (typeof Z_TABS)[number];

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...shiftsSearchShape,
  tab: z.enum(Z_TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  device: z.string().uuid().optional().catch(undefined),
  mismatch: z.enum(["true"]).optional().catch(undefined),
  ...quickActionSearch,
});

export const Route = createFileRoute("/_app/vardiya/z-raporlari")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ZReportsPage,
});

/**
 * Z raporları ve ÖKC cihazları.
 *
 * Karşılaştırma CİHAZ bazında DEĞİL, KAPSAM toplamı bazındadır (ADR 0013 karar 13): aynı kapsamda birden
 * çok cihaz varsa satırda "grup karşılaştırması" rozeti çıkar. Gün henüz tamamlanmadıysa sonuç GEÇİCİDİR.
 */
function ZReportsPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftZReports")}
      description={t("shifts.zReports.description")}
      breadcrumb={[{ label: t("menu.shiftZReports") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <ZReportsBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function ZReportsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useShiftStation(companyId);
  const tab: ZTab = search.tab ?? "reports";

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <Tabs
      className="grid gap-4"
      value={tab}
      onValueChange={(next) =>
        void navigate({
          to: ".",
          search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
          replace: true,
        })
      }
    >
      <TabsList aria-label={t("shifts.zReports.tabsLabel")}>
        {Z_TABS.map((value) => (
          <TabsTrigger key={value} value={value}>
            {t(`shifts.zReports.tab.${value}`)}
          </TabsTrigger>
        ))}
      </TabsList>
      {/* İçerik MUTLAKA `TabsContent` içinde: tetikleyicinin `aria-controls` hedefi var olmalı. */}
      <TabsContent value={tab} className="grid gap-4 pt-0">
        {tab === "reports" ? (
          <ReportsTab companyId={companyId} stationId={station.stationId} station={station} />
        ) : null}
        {tab === "devices" ? <DevicesTab companyId={companyId} station={station} /> : null}
      </TabsContent>
    </Tabs>
  );
}

type StationSelection = ReturnType<typeof useShiftStation>;

function ReportsTab({
  companyId,
  stationId,
  station,
}: {
  companyId: string;
  stationId: string;
  station: StationSelection;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useShiftPermissions();
  const [entering, setEntering] = useState(false);
  useOpenFromQuickAction(search.yeni === "1", () => setEntering(true), permissions.canManageZReports);
  const [selected, setSelected] = useState<ZReport | null>(null);
  const [resolving, setResolving] = useState(false);

  const setFilters = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const devices = useFiscalDevices(companyId, stationId, true);
  const filters = {
    stationId,
    from: search.from,
    to: search.to,
    fiscalDeviceId: search.device,
    mismatchOnly: search.mismatch === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useZReports(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<ZReport>[]>(
    () => [
      {
        id: "businessDate",
        header: t("shifts.fields.businessDate"),
        hideable: false,
        exportValue: (row) => row.businessDate,
        cell: (row) => formatDate(row.businessDate),
      },
      {
        id: "device",
        header: t("shifts.zReports.fields.device"),
        exportValue: (row) => `${row.fiscalDeviceSerialNo} ${row.fiscalDeviceLabel}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-mono">{row.fiscalDeviceSerialNo}</span>
            <span className="text-xs text-muted-foreground">{row.fiscalDeviceLabel}</span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("shifts.zReports.fields.scope"),
        exportValue: (row) => FISCAL_DEVICE_SCOPE_LABELS[row.scope],
        cell: (row) => (
          <StatusBadge tone={FISCAL_DEVICE_SCOPE_TONE[row.scope]}>
            {FISCAL_DEVICE_SCOPE_LABELS[row.scope]}
          </StatusBadge>
        ),
      },
      {
        id: "zNo",
        header: t("shifts.zReports.fields.zNo"),
        align: "right",
        exportValue: (row) => String(row.zNo),
        cell: (row) => formatCount(row.zNo),
      },
      {
        id: "totalAmount",
        header: t("shifts.zReports.fields.totalAmount"),
        align: "right",
        exportValue: (row) => row.totalAmount,
        cell: (row) => <MoneyText value={row.totalAmount} />,
      },
      {
        id: "systemAmount",
        header: t("shifts.zReports.fields.systemAmount"),
        align: "right",
        exportValue: (row) => row.systemAmount ?? "",
        cell: (row) => (row.systemAmount === null ? "—" : <MoneyText value={row.systemAmount} />),
      },
      {
        id: "difference",
        header: t("shifts.zReports.fields.difference"),
        exportValue: (row) => row.difference ?? "",
        // Renk TEK BAŞINA anlam taşımaz: yön metni ve işaretli tutar birlikte yazılır.
        cell: (row) => (
          <span className="grid gap-0.5">
            <StatusBadge tone={zDifferenceTone(row)}>
              {t(`shifts.zReports.direction.${zDifferenceDirection(row.difference)}`)}
            </StatusBadge>
            <span className="tabular">{formatMinor(row.difference)}</span>
          </span>
        ),
      },
      {
        id: "flags",
        header: t("common.status"),
        exportValue: (row) => zReportFlags(row).join(", "),
        cell: (row) => {
          const flags = zReportFlags(row);
          return (
            <span className="flex flex-wrap gap-1">
              {row.mismatch && row.resolvedAt === null ? (
                <StatusBadge tone="danger">{t("shifts.zReports.status.MISMATCH")}</StatusBadge>
              ) : null}
              {flags.map((flag) => (
                <StatusBadge key={flag} tone={flag === "provisional" ? "warning" : "neutral"}>
                  {t(`shifts.zReports.flag.${flag}`)}
                </StatusBadge>
              ))}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: t("common.actions"),
        hideable: false,
        cell: (row) => (
          <Button variant="outline" size="sm" onClick={() => setSelected(row)}>
            {t("shifts.zReports.detail")}
          </Button>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      {permissions.canManageZReports ? (
        <div className="flex justify-end">
          <Button onClick={() => setEntering(true)}>
            <Plus aria-hidden="true" />
            {t("shifts.zReports.form.title")}
          </Button>
        </div>
      ) : null}

      <DataTable
        caption={t("shifts.zReports.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ReceiptText />}
            title={t("shifts.zReports.emptyTitle")}
            description={t("shifts.zReports.emptyDescription")}
          />
        }
        filters={
          <>
            <ShiftStationField selection={station} />
            <FormField label={t("shifts.zReports.fields.device")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={search.device ?? ""}
                  onChange={(event) => setFilters({ device: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {(devices.data ?? []).map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.serialNo} — {device.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("shifts.list.from")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.from ?? null}
                  onValueChange={(value) => setFilters({ from: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("shifts.list.to")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.to ?? null}
                  onValueChange={(value) => setFilters({ to: value ?? undefined })}
                />
              )}
            </FormField>
            <label className="flex min-h-6 items-center gap-2 pb-2 text-sm">
              <Checkbox
                checked={search.mismatch === "true"}
                onChange={(event) => setFilters({ mismatch: event.target.checked ? "true" : undefined })}
              />
              {t("shifts.zReports.mismatchOnly")}
            </label>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />

      <ZReportDialog
        open={entering}
        onOpenChange={setEntering}
        companyId={companyId}
        devices={devices.data ?? []}
        defaultBusinessDate={search.to ?? search.from ?? yesterday()}
      />
      {/* Yan panel ve "farkı çöz" penceresi ASLA aynı anda açılmaz: iç içe iki kalıcı katman odağı böler. */}
      <ZReportPanel
        companyId={companyId}
        report={resolving ? null : selected}
        onClose={() => setSelected(null)}
        onResolve={() => setResolving(true)}
      />
      <ResolveZReportDialog
        open={resolving}
        onOpenChange={(next) => {
          setResolving(next);
          if (!next) setSelected(null);
        }}
        companyId={companyId}
        report={selected}
      />
    </>
  );
}

function yesterday(): string {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

function ZReportPanel({
  companyId,
  report,
  onClose,
  onResolve,
}: {
  companyId: string;
  report: ZReport | null;
  onClose: () => void;
  onResolve: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshShifts();
  const permissions = useShiftPermissions();

  const recompare = useMutation({
    mutationFn: async () =>
      (await api.POST("/shifts/z-reports/{id}/recompare", { params: { path: { id: report!.id } } })).data!,
    onSuccess: async () => {
      await refresh(companyId);
      toast({ tone: "success", title: t("shifts.zReports.recompared") });
      onClose();
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <Drawer
      open={report !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={report ? `Z ${report.zNo}` : ""}
      description={report ? `${report.fiscalDeviceSerialNo} — ${formatDate(report.businessDate)}` : undefined}
      footer={
        report && permissions.canManageZReports ? (
          <>
            <Button variant="outline" loading={recompare.isPending} onClick={() => recompare.mutate()}>
              <RefreshCw aria-hidden="true" />
              {t("shifts.zReports.recompare")}
            </Button>
            <Button disabled={!report.mismatch || report.resolvedAt !== null} onClick={onResolve}>
              {t("shifts.zReports.resolve.action")}
            </Button>
          </>
        ) : null
      }
    >
      {report === null ? null : (
        <div className="grid gap-4">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <PanelField label={t("shifts.zReports.fields.scope")}>
              {FISCAL_DEVICE_SCOPE_LABELS[report.scope]}
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.reportAt")}>
              {formatDateTime(report.reportAt)}
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.totalAmount")}>
              <MoneyText value={report.totalAmount} />
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.systemAmount")}>
              {report.systemAmount === null ? "—" : <MoneyText value={report.systemAmount} />}
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.difference")}>
              <span className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={zDifferenceTone(report)}>
                  {t(`shifts.zReports.direction.${zDifferenceDirection(report.difference)}`)}
                </StatusBadge>
                <span className="tabular">{formatMinor(report.difference)}</span>
              </span>
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.receiptCount")}>
              {formatCount(report.receiptCount)}
            </PanelField>
            <PanelField label={t("shifts.zReports.fields.source")}>
              {Z_REPORT_SOURCE_LABELS[report.source]}
            </PanelField>
            <PanelField label={t("common.status")}>
              <span className="flex flex-wrap gap-1">
                {zReportFlags(report).map((flag) => (
                  <StatusBadge key={flag} tone={flag === "provisional" ? "warning" : "neutral"}>
                    {t(`shifts.zReports.flag.${flag}`)}
                  </StatusBadge>
                ))}
              </span>
            </PanelField>
          </dl>

          {report.resolutionNote ? (
            <Alert tone="info" title={t("shifts.zReports.resolve.noteTitle")}>
              {report.resolutionNote}
            </Alert>
          ) : null}

          <SimpleTable
            caption={t("shifts.zReports.form.vatBreakdown")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("shifts.zReports.fields.ratePercent")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.zReports.fields.gross")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.zReports.fields.vat")}
                </th>
              </tr>
            }
          >
            {report.vatBreakdown.length === 0 ? (
              <tr className="border-t">
                <td className={tdClass} colSpan={3}>
                  {t("shifts.zReports.noBreakdown")}
                </td>
              </tr>
            ) : (
              report.vatBreakdown.map((line) => (
                <tr key={line.ratePercent} className="border-t">
                  <td className={tdClass}>%{line.ratePercent}</td>
                  <td className={tdRightClass}>
                    <MoneyText value={line.gross} />
                  </td>
                  <td className={tdRightClass}>
                    <MoneyText value={line.vat} />
                  </td>
                </tr>
              ))
            )}
          </SimpleTable>

          <SimpleTable
            caption={t("shifts.zReports.form.paymentBreakdown")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("shifts.zReports.fields.paymentKind")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.records.amount")}
                </th>
              </tr>
            }
          >
            {Object.keys(report.paymentBreakdown).length === 0 ? (
              <tr className="border-t">
                <td className={tdClass} colSpan={2}>
                  {t("shifts.zReports.noBreakdown")}
                </td>
              </tr>
            ) : (
              Object.entries(report.paymentBreakdown).map(([kind, amount]) => (
                <tr key={kind} className="border-t">
                  <td className={tdClass}>{kind}</td>
                  <td className={tdRightClass}>
                    <MoneyText value={amount} />
                  </td>
                </tr>
              ))
            )}
          </SimpleTable>
        </div>
      )}
    </Drawer>
  );
}

function PanelField({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function DevicesTab({ companyId, station }: { companyId: string; station: StationSelection }): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  const devices = useFiscalDevices(companyId, station.stationId);
  const [editing, setEditing] = useState<FiscalDevice | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <ShiftStationField selection={station} />
        {permissions.canManageZReports ? (
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t("shifts.zReports.device.newTitle")}
          </Button>
        ) : null}
      </Card>

      {devices.error ? (
        <Alert tone="danger">{errorMessage(devices.error)}</Alert>
      ) : devices.isPending ? (
        <SkeletonRows rows={4} />
      ) : (devices.data ?? []).length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<ReceiptText />}
          title={t("shifts.zReports.device.emptyTitle")}
          description={t("shifts.zReports.device.emptyDescription")}
        />
      ) : (
        <SimpleTable
          caption={t("shifts.zReports.device.caption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("shifts.zReports.device.serialNo")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.zReports.device.label")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.station")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.zReports.device.scope")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {(devices.data ?? []).map((device) => (
            <tr key={device.id} className="border-t">
              <td className={`${tdClass} font-mono`}>{device.serialNo}</td>
              <td className={tdClass}>{device.label}</td>
              <td className={tdClass}>{device.stationName}</td>
              <td className={tdClass}>
                <StatusBadge tone={FISCAL_DEVICE_SCOPE_TONE[device.scope]}>
                  {FISCAL_DEVICE_SCOPE_LABELS[device.scope]}
                </StatusBadge>
              </td>
              <td className={tdClass}>
                <StatusBadge tone={device.isActive ? "success" : "neutral"}>
                  {device.isActive ? t("common.active") : t("common.inactive")}
                </StatusBadge>
              </td>
              <td className={tdClass}>
                {permissions.canManageZReports ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(device)}>
                    {t("common.edit")}
                  </Button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}

      <FiscalDeviceDialog
        open={adding}
        onOpenChange={setAdding}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
        device={null}
      />
      <FiscalDeviceDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
        device={editing}
      />
    </>
  );
}
